import { selectHasCreatedNewAnnotation } from "@/store/create-annotation-slice";
import { selectIsLocationMarkerCreationEnabled } from "@/store/location-marker-slice";
import {
  selectActiveMeasurement,
  selectPanoMeasurementToolState,
  selectSessionMeasurements,
} from "@/store/measurement-tool-selector";
import { PanoMeasurementToolState } from "@/store/measurement-tool-slice";
import { selectNumberOfSessionAnalyses } from "@/store/point-cloud-analysis-tool-selector";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import {
  ContextualBanner,
  cyan,
  FaroText,
} from "@faro-lotv/app-component-toolbox";
import { isMacOS } from "@faro-lotv/foundation";
import { Link, Stack, SxProps, Theme } from "@mui/material";
import { ReactNode, useState, type JSX } from "react";
import { AvailableAnnotationTools } from "../../r3f/renderers/annotations/annotations-types";
import { AccountSettingsDialog } from "../account-settings-dialog";

type Props = {
  /** Additional styling props */
  sx?: SxProps<Theme>;
};

type GeneralHelpBannerProps = Props & {
  /** Text to show in the banner */
  text: ReactNode;

  /** Name of the tool to check. When the tool is activated, the banner will be shown */
  toolToCheck: ToolName;

  /** If true, then the banner will be hidden. @default false */
  hidden?: boolean;
};

function GeneralHelpBanner({
  text,
  toolToCheck,
  hidden,
  sx,
}: GeneralHelpBannerProps): JSX.Element | null {
  const activeTool = useAppSelector(selectActiveTool);

  if (activeTool !== toolToCheck || hidden) return null;
  return <ContextualBanner sx={sx}>{text}</ContextualBanner>;
}

/**
 * @returns a help message banner which can be used for informing how the measurement can be performed
 */
export function MeasurementHelpBanner({ sx }: Props): JSX.Element | null {
  const activeMeasure = useAppSelector(selectActiveMeasurement);
  const isFirstMeasurement = useAppSelector(selectSessionMeasurements) === 0;
  const [isSettingPageOpen, setIsSettingPageOpen] = useState<boolean>(false);
  const panoMeasureState = useAppSelector(selectPanoMeasurementToolState);

  return (
    <>
      <GeneralHelpBanner
        text={
          <Stack spacing={0.25}>
            {panoMeasureState === PanoMeasurementToolState.None && (
              <>
                <FaroText variant="bodyL" dark>
                  Hold <b>{isMacOS() ? "CMD" : "CTRL"}</b> to snap to edges or
                  corners.
                </FaroText>
                <FaroText variant="bodyL" dark>
                  Hold <b>SHIFT</b> to measure horizontal or vertical distances.
                </FaroText>
              </>
            )}
            <FaroText variant="bodyL" dark>
              To complete the measurement, press <b>Esc</b> key, or close a
              polygon by clicking the first point.
            </FaroText>
            <FaroText variant="bodyL" dark>
              To change units, open the <b>top-right profile menu</b>, go to{" "}
              <b>Settings</b>, then select{" "}
              <Link
                sx={{
                  color: cyan[400],
                  fontWeight: "bold",
                  pointerEvents: "auto",
                  "&:hover": {
                    cursor: "pointer",
                  },
                }}
                onClick={() => setIsSettingPageOpen(true)}
              >
                Language & units
              </Link>
              .
            </FaroText>
          </Stack>
        }
        toolToCheck={ToolName.measurement}
        hidden={!!activeMeasure || !isFirstMeasurement}
        sx={sx}
      />
      {isSettingPageOpen && (
        <AccountSettingsDialog
          onClose={() => setIsSettingPageOpen(false)}
          openTab="languageAndUnit"
        />
      )}
    </>
  );
}

interface CreateAnnotationHelpBannerProps extends Props {
  /** Available tool/s to which the help text needs to be updated */
  availableTool: AvailableAnnotationTools;
}

/**
 * @returns a help message banner which can be used for informing how to create a new Annotation
 */
export function CreateAnnotationHelpBanner({
  availableTool,
  sx,
}: CreateAnnotationHelpBannerProps): JSX.Element | null {
  const hasCreatedNewAnnotation = useAppSelector(selectHasCreatedNewAnnotation);

  let helpText = "";
  switch (availableTool) {
    case AvailableAnnotationTools.rectangle:
      helpText = "Click and drag to draw the annotation rectangle.";
      break;
    case AvailableAnnotationTools.singlePoint:
      helpText =
        "Click the point at which you want to insert a point annotation.";
      break;
    case AvailableAnnotationTools.all:
      helpText =
        "Click to insert a point annotation or click and drag to insert an area annotation.";
      break;
  }
  return (
    <GeneralHelpBanner
      text={helpText}
      toolToCheck={ToolName.annotation}
      hidden={hasCreatedNewAnnotation}
      sx={sx}
    />
  );
}

/**
 * @returns a help message banner which can be used for informing how the analysis can be performed
 */
export function AnalysisHelpBanner({ sx }: Props): JSX.Element | null {
  const isFirstAnalysis = useAppSelector(selectNumberOfSessionAnalyses) === 0;

  return (
    <GeneralHelpBanner
      text="Close the polygon by clicking the first point."
      toolToCheck={ToolName.analysis}
      hidden={!isFirstAnalysis}
      sx={sx}
    />
  );
}

/**
 * @returns a help message banner for the edit waypoints tool
 */
export function EditWaypointsHelpBanner({ sx }: Props): JSX.Element {
  const isLocationMarkerCreationEnabled = useAppSelector(
    selectIsLocationMarkerCreationEnabled,
  );

  return (
    <GeneralHelpBanner
      text={
        isLocationMarkerCreationEnabled
          ? "Click to add location markers"
          : "Drag waypoints to reposition them"
      }
      toolToCheck={ToolName.editWaypoints}
      sx={sx}
    />
  );
}
